import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LutonCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Luton Pothole Claims | 411km of Residential Roads | Fixtyer"
        description="Luton Borough Council maintains 411km of residential U-roads — 86% of its 480km network — with the greatest backlog. DfT rates it AMBER overall despite GREEN spend. See what that means for your pothole claim."
        canonical="/councils/luton"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Luton', url: '/councils/luton' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Luton spends more than double its DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and road condition — and Luton is AMBER overall because amber-category roads now dominate the classified network. Section 58 depends on whether the pothole that damaged your vehicle was reasonably inspected and repaired in time, not on aggregate spending.',
          },
          {
            question: 'What if my pothole was on a residential U-road?',
            answer: 'U-roads make up 411km — 86% of Luton\'s 480km carriageway network. The council\'s own report states residential roads "have the greatest backlog." U-road RED condition has fallen from 8.7% in 2021 to 6.7% in 2024, but the council does not publish amber or green percentages for U-roads, only the red category.',
          },
          {
            question: 'Does Luton\'s yearly HD camera survey weaken my claim?',
            answer: 'It strengthens the council\'s Section 58 paperwork — Luton surveys its entire network annually with high-definition cameras. But survey data showing 58% of A-roads and 63% of B/C roads in amber condition is evidence that maintenance may soon be required across large parts of the network. Your claim still lives on whether your specific defect was acted on.',
          },
          {
            question: 'What does the council\'s amber-roads challenge mean for Section 58?',
            answer: 'Luton admits its "biggest challenge" is amber roads — surfaces where maintenance may be required soon and which deteriorate to red if left untreated. That is documented knowledge that defects can form on roads the council already knows are borderline. Combined with 4,159 carriageway defect repairs in four years, it supports arguments that reactive patching does not always keep pace.',
          },
          {
            question: 'Are the pothole repair figures reliable?',
            answer: 'Treat them with care. Luton\'s own footnote states it does not have a separate pothole count — repairs are categorised as "carriageway defects repaired and not potholes repaired." The published figures (1,033 in 2021/22 through 1,036 in 2024/25) are defect repairs, not a precise pothole tally.',
          },
          {
            question: 'What changes when PAS 2161 starts in 2026?',
            answer: 'From 2026 Luton will adopt the BSI PAS 2161 standard, moving from three condition categories to five. The council notes this will give government a more detailed picture of road condition. Pre- and post-2026 figures may not be directly comparable — worth noting if your incident spans the methodology change.',
          },
          {
            question: 'Does GREEN best practice mean my claim will fail?',
            answer: 'Not automatically. GREEN best practice reflects asset management processes — Eastern Highways Alliance benchmarking, lifecycle planning, BSI accreditation. Against that documented system, generic claims struggle. Claims with prior reports, photos showing defect age, and evidence the pothole met intervention criteria before you hit it remain viable.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Luton Borough Council"
        councilSlug="luton"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/luton"
        headline="Luton Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Luton</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Luton: 411km of Residential Roads Carry the Greatest Backlog
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Luton is a compact 480km network where <strong className="text-amber-400">86% is residential U-roads</strong> — 
            and the council's own report says those roads have the greatest backlog. The DfT rates Luton 
            <strong className="text-amber-400"> AMBER overall</strong> despite GREEN spend and best-practice scorecards, 
            because <strong className="text-amber-400">63% of B/C roads and 58% of A-roads</strong> are now in amber 
            condition, with green-category surfaces falling year on year.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">411km</div>
            <div className="text-white font-medium mt-2">Residential U-roads — 86% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              Against just 49km of A-roads and 20km of B/C roads. Luton Council states U-roads 
              "have the greatest backlog" — the roads most residents drive every day.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What the condition data shows
          </h2>
          <p className="text-surface-600 mb-8">
            Four years of SCANNER survey data from Luton Borough Council's transparency report — 
            red roads stable, amber roads taking over
          </p>

          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (49km — 10.2% of network): amber majority emerging</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">3.2%</td>
                    <td className="px-4 py-2 text-center">48.4%</td>
                    <td className="px-4 py-2 text-center">48.3%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">3.9%</td>
                    <td className="px-4 py-2 text-center">50.7%</td>
                    <td className="px-4 py-2 text-center">45.4%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">4.0%</td>
                    <td className="px-4 py-2 text-center">52.7%</td>
                    <td className="px-4 py-2 text-center">43.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.0%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">58.1%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">38.9%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Green A-roads have fallen from 48.3% to 38.9% in four years. Amber now covers 
              well over half the borough's main roads — surfaces where the council itself says 
              "maintenance may be required soon."
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (20km — 4.2% of network): amber now dominates</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">7.4%</td>
                    <td className="px-4 py-2 text-center">55.0%</td>
                    <td className="px-4 py-2 text-center">37.6%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6.1%</td>
                    <td className="px-4 py-2 text-center">54.7%</td>
                    <td className="px-4 py-2 text-center">39.2%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">7.8%</td>
                    <td className="px-4 py-2 text-center">56.4%</td>
                    <td className="px-4 py-2 text-center">35.7%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold">5.6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">62.8%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">31.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Green B/C roads have dropped from 37.6% to 31.2%. Nearly two-thirds of Luton's 
              classified local roads are now amber — and the council's strategy allocates 35% of 
              capital budget to treating amber roads from 2025–28, acknowledging the scale of the problem.
            </p>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">Well funded, still AMBER</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£2.85m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.4m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">74%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Luton projects spending more than <span className="text-emerald-400">double its DfT allocation</span> — 
              earning a GREEN spend scorecard — yet condition stays AMBER because amber-category roads 
              are expanding faster than preventative treatments can hold them. The chequebook is not the bottleneck; 
              the amber backlog is.
            </p>
          </div>
        </div>
      </section>

      {/* U-road residential backlog */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 411km residential majority
          </h2>
          <p className="text-surface-600 mb-8">
            86% of Luton's carriageway network is U-roads — and the council names them as the greatest backlog
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">8.7%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">9.9%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7.8%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">6.7%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the council publishes — and what it does not</h3>
              <p className="text-surface-600 text-sm mb-3">
                Luton undertakes a yearly high-definition camera survey of its entire road network. 
                For U-roads, however, the transparency report publishes only the RED percentage — 
                not amber or green breakdowns. You know 6.7% of residential roads were red in 2024, 
                but not how many were borderline amber.
              </p>
              <p className="text-surface-600 text-sm">
                At 6.7% red, that is roughly <strong>28km of residential roads</strong> needing maintenance 
                on a 411km network — before counting any amber surfaces the council has not quantified publicly.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The backlog admission</h3>
              <p className="text-amber-800 text-sm mb-3">
                Luton states the condition of its network is "steadily improving" — yet names U-roads 
                as having "the greatest backlog." From 2025–28, 65% of capital budget targets 
                red-indicator roads with full resurfacing, while 35% treats amber roads with surface 
                dressing, micro asphalt and preservation.
              </p>
              <p className="text-amber-800 text-sm">
                If your pothole was on a residential street, you are on the road type the council 
                itself identifies as its hardest problem.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why this matters for Section 58</h3>
            <p className="text-brand-200 mb-4">
              Luton's yearly HD survey gives it stronger network knowledge than councils that inspect 
              residential roads less frequently. But for your claim, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your U-road in the red 6.7% — or an unreported amber surface?</li>
              <li>• Did the council's preventative programme reach your street before the defect formed?</li>
              <li>• If 65% of capital targets red roads, what happens to amber roads that deteriorate in the meantime?</li>
              <li>• Does a carriageway defect repair count as fixing the underlying road condition?</li>
            </ul>
            <p className="text-white font-medium">
              Annual surveys help the council — but documented amber expansion on classified roads 
              and an admitted residential backlog still leave room to challenge whether your 
              specific defect was reasonably managed.
            </p>
          </div>
        </div>
      </section>

      {/* Defect repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            4,159 carriageway defect repairs in four years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive maintenance still accounts for 26% of projected 2025/26 spend
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway defects repaired*</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Reactive maintenance share</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">1,033</td>
                  <td className="px-6 py-4 text-center">39%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">846</td>
                  <td className="px-6 py-4 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,244</td>
                  <td className="px-6 py-4 text-center">31%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">1,036</td>
                  <td className="px-6 py-4 text-center">32%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2025 to present</td>
                  <td className="px-6 py-4 text-center">157</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total (2021/22–2024/25)</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,159</td>
                  <td className="px-6 py-4 text-center text-surface-400">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~3 defects repaired every day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over four full years, Luton repairs roughly three carriageway defects per day 
                across its 480km network. A network producing defects at that rate is one where 
                potholes can form between survey cycles and preventative programmes — exactly where 
                prior reports and photographic evidence decide claims.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">*The counting caveat</h3>
              <p className="text-amber-800 text-sm">
                Luton states: "We do not have separate number for potholes filled. We have data for 
                number of potholes reported and repairs are categorised as carriageway defects repaired 
                and not potholes repaired." The published figures understate individual pothole counts 
                if multiple defects are patched in one visit.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Amber roads challenge */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The amber-roads challenge
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for why condition stays AMBER despite rising investment
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The biggest challenge for the authority is what is termed amber roads, as if these are left untreated, they deteriorate to red which is at the point where expensive resurfacing needs to take place. By intervening with surface treatments such as asphalt preservation, surface dressing and micro asphalt we can prevent those amber roads turning into red for up to ten years."
              source="Luton Borough Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <QuoteCard
              quote="The condition of all of Luton's road network is steadily improving without preventative maintenance approach including the U (residential) roads which have the greatest backlog."
              source="Luton Borough Council Local Highways Maintenance Transparency Report (June 2025) — wording as published"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What this admission means</h3>
              <p className="text-surface-600 text-sm mb-3">
                Luton formally acknowledges amber roads are its primary maintenance challenge — 
                surfaces where intervention is needed before full resurfacing becomes unavoidable. 
                That is documented knowledge that borderline roads exist across the network.
              </p>
              <p className="text-surface-600 text-sm">
                With 58% of A-roads and 63% of B/C roads already amber in 2024, the scale of 
                that challenge is quantified — not hypothetical.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions worth asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your road amber at the last survey — meaning maintenance "may be required soon"?</li>
                <li>• Did surface dressing, micro asphalt or preservation reach your street in time?</li>
                <li>• If amber roads become red when untreated, why was your defect still there?</li>
                <li>• Does a reactive patch on a deteriorating amber road count as reasonable maintenance?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming against a well-funded AMBER council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Luton invests seriously — here's how that shapes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projected capital spend more than double DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN best practice — lifecycle planning, Eastern Highways Alliance benchmarking, BSI accreditation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Yearly HD camera survey of the entire network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 74% of 2025/26 spend estimated as preventative
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road red condition improving — 8.7% to 6.7% since 2021
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence. Generic claims will struggle against this system.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 58% of A-roads and 63% of B/C roads now amber
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 411km of U-roads with the "greatest backlog" — 86% of the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Green-category roads falling on A and B/C routes year on year
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,159 carriageway defect repairs in four years — defects still forming
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits amber roads are the biggest challenge if left untreated
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and best-practice scorecards, your claim lives or dies on 
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the residential backlog admission and incomplete condition data</li>
              <li>• Whether the defect exceeded the council's own intervention thresholds before you hit it</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and 
              cites Luton Borough Council's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Report a pothole in Luton
          </h2>
          <p className="text-surface-600 mb-8">
            Reporting the defect creates a council record — useful evidence whether or not you claim
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Council reporting channels</h3>
              <p className="text-surface-600 text-sm mb-4">
                Luton aims to inspect pothole reports within two working days. The council operates 
                highway inspectors, a seek-and-repair gang, and a 24-hour response team. Online reports 
                allow photo uploads and progress tracking.
              </p>
              <ul className="text-sm space-y-2">
                <li>
                  <a
                    href="https://www.luton.gov.uk/report"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Report a road or pavement problem online
                  </a>
                  <span className="text-surface-500"> — potholes, pavements, drains and more</span>
                </li>
                <li>
                  <a
                    href="https://www.luton.gov.uk/Transport_and_streets/Road-maintenance-potholes-and-gritting/potholes/Pages/default.aspx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-600 hover:underline font-medium"
                  >
                    Potholes information page
                  </a>
                  <span className="text-surface-500"> — repair thresholds and response times</span>
                </li>
                <li>
                  <span className="text-brand-600 font-medium">highways@luton.gov.uk</span>
                  <span className="text-surface-500"> — email reports with photos</span>
                </li>
                <li>
                  <span className="text-brand-600 font-medium">01582 510333</span>
                  <span className="text-surface-500"> — dangerous potholes needing immediate attention</span>
                </li>
              </ul>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Intervention thresholds</h3>
              <p className="text-amber-800 text-sm mb-3">
                Luton states it generally repairs potholes 30mm or deeper on main roads and 50mm or 
                deeper on residential roads. Potholes over 50mm deep on main roads are repaired within 
                24 hours of inspection.
              </p>
              <p className="text-amber-800 text-sm">
                If you are claiming for damage, photograph the pothole with a reference object for scale. 
                Evidence it met the council's own depth threshold before you hit it strengthens your case 
                that repair should have happened sooner.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-xl p-6">
            <p className="text-brand-200 text-sm">
              A dated report with photos creates a council record the highways team must respond to. 
              For a future claim — or to establish notice before damage occurs — documented reports 
              are stronger evidence than an undocumented pothole the council can say it never knew about.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a pothole in Luton?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No residential backlog argument</li>
                <li>• No prior-report search</li>
                <li>• No amber-roads challenge cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 411km U-road backlog documented</li>
                <li>✅ Amber-majority condition data cited</li>
                <li>✅ 4,159 defect repairs in four years referenced</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Luton</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=luton"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order my Luton claim pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Luton spends more than double its DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and 
                road condition — and Luton is AMBER overall because amber-category roads now dominate the 
                classified network. Section 58 depends on whether the pothole that damaged your vehicle 
                was reasonably inspected and repaired in time, not on aggregate spending.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential U-road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 411km — 86% of Luton's 480km carriageway network. The council's own report 
                states residential roads "have the greatest backlog." U-road RED condition has fallen from 
                8.7% in 2021 to 6.7% in 2024, but the council does not publish amber or green percentages 
                for U-roads, only the red category.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Luton's yearly HD camera survey weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It strengthens the council's Section 58 paperwork — Luton surveys its entire network annually 
                with high-definition cameras. But survey data showing 58% of A-roads and 63% of B/C roads in 
                amber condition is evidence that maintenance may soon be required across large parts of the 
                network. Your claim still lives on whether your specific defect was acted on.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What does the council's amber-roads challenge mean for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                Luton admits its "biggest challenge" is amber roads — surfaces where maintenance may be 
                required soon and which deteriorate to red if left untreated. That is documented knowledge 
                that defects can form on roads the council already knows are borderline. Combined with 
                4,159 carriageway defect repairs in four years, it supports arguments that reactive 
                patching does not always keep pace.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Are the pothole repair figures reliable?
              </h3>
              <p className="text-surface-600 text-sm">
                Treat them with care. Luton's own footnote states it does not have a separate pothole count — 
                repairs are categorised as "carriageway defects repaired and not potholes repaired." The 
                published figures (1,033 in 2021/22 through 1,036 in 2024/25) are defect repairs, not a 
                precise pothole tally.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What changes when PAS 2161 starts in 2026?
              </h3>
              <p className="text-surface-600 text-sm">
                From 2026 Luton will adopt the BSI PAS 2161 standard, moving from three condition categories 
                to five. The council notes this will give government a more detailed picture of road condition. 
                Pre- and post-2026 figures may not be directly comparable — worth noting if your incident spans 
                the methodology change.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does GREEN best practice mean my claim will fail?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. GREEN best practice reflects asset management processes — Eastern Highways 
                Alliance benchmarking, lifecycle planning, BSI accreditation. Against that documented system, 
                generic claims struggle. Claims with prior reports, photos showing defect age, and evidence the 
                pothole met intervention criteria before you hit it remain viable.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.luton.gov.uk/Transport_and_streets/Lists/LutonDocuments/PDF/Highways/local-highways-maintenance-transparency-report.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Luton Borough Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.{' '}
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
